import { NgClass, NgForOf, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTimepicker, MatTimepickerInput, MatTimepickerToggle } from '@angular/material/timepicker';
import { MatTooltip } from '@angular/material/tooltip';
import { environment } from '../../../environments/environment';
import { AdminService } from '../../admin/admin.service';
import { RippleDirective } from '../../ripple.directive';
import { DataToolsLiteMessage } from '../../shared/DataToolsLiteMessage';
import { DatatoolsLiteService } from '../../shared/services/datatools-lite.service';
import { MessageNotificationService } from '../../shared/services/message-notification.service';
import { DataCleanerService } from '../data-cleaner.service';
import { IColumns } from '../models/columns-model';
import { DataCleanerSearch, SearchProperties } from '../models/data-cleaner-search.model';
import { CandidateOptionsService } from './candidate-options.service';
import { FilterOptionToggleComponent } from './filter-option-toggle/filter-option-toggle.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-candidate-options',
  templateUrl: './candidate-options.component.html',
  imports: [
    FilterOptionToggleComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    ReactiveFormsModule,
    NgClass,
    NgIf,
    MatTooltip,
    MatInput,
    FormsModule,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatSlideToggle,
    NgForOf,
    MatAutocompleteTrigger,
    MatAutocomplete,
    RippleDirective,
    MatLabel,
    MatTimepickerInput,
    MatTimepicker,
    MatTimepickerToggle,
    MatSuffix
  ],
  styleUrls: ['./candidate-options.component.scss']
})
// Full disclosure, this component is riddled with awful code. My honest opinion is that the entire DataCleaner UI needs to be burned down
// and rewritten from scratch. But that's never going to happen because no one uses it.
export class CandidateOptionsComponent implements OnInit {
  blankSearch = {
    searchName: '',
    activityFilters: [],
    scheduledTime: null,
  };
  noteActions = [];
  postSearchActions = {
    EMAIL_NOTIFICATION: false,
    CREATE_TEARSHEET: false,
    RUN_AGAIN: false,
  };
  peopleGroups = {
    format: '$label',
    options: []
  };
  tearsheetGroups = {
    dateAdded: [],
    dateLastComment: [],
    dateLastActivity: []
  };
  runAgainOptions = {
    repeatDays: 1
  };
  dayIntervals = {
    options: [
      { value: '', label: '' },
      { value: 1, label: '1 day' },
      { value: 7, label: '7 days' },
      { value: 14, label: '2 weeks' },
      { value: 30, label: '30 Days' },
      { value: 60, label: '60 Days' },
      { value: 90, label: '90 Days' }
    ]
  };

  scheduledDate: Date | null = null;
  scheduledTime: string;
  scheduledDateTime: Date | null = null;
  numRecordsFound;
  emailNotification;
  tearsheetOwner;
  scheduledOptions = [{label: 'Now', value: 'now'}, {label: 'Later', value: 'later'}];
  scheduledSearch = 'now';
  users = [];
  currentUser;
  cleanOptionsForm: FormGroup;
  cleanOptions: SearchProperties = new SearchProperties();
  search = this.blankSearch;
  showCsvExport = false;
  isScheduled = false;
  tearsheetOwnerDisplay: string;
  filteredUsers: any[] = [];
  private calculateTotalSubscription: Subscription;

  @Input() isLoading: boolean;
  @Input() isCandidate;
  @Input() currentSearchId: string;
  @Input() showFilterOptions: boolean;
  @Input() tearsheetName: string;
  @Output() hotListName: EventEmitter<any> = new EventEmitter<any>();
  @Output() newReportRun = new EventEmitter<any>();
  @Output() scheduledSearchEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() runSearchRequested = new EventEmitter<any>();
  @Output() searchIdChange = new EventEmitter<string>();

  constructor(private formBuilder: FormBuilder,
              private http: HttpClient,
              private dataCleanerService: DataCleanerService,
              private candidateOptionsService: CandidateOptionsService,
              private adminService: AdminService,
              private datatoolsLiteService: DatatoolsLiteService,
              private messageNotificationService: MessageNotificationService) {
    this.createForm();
    this.candidateOptionsService.getCurrentUser().subscribe(resp => {
      this.currentUser = resp;
    });
    this.candidateOptionsService.getUsers().subscribe((resp: any) => {
      this.users = [...resp['users']];
      this.filteredUsers = [...resp['users']];
    });
    this.candidateOptionsService.getNoteTypes().subscribe(resp => {
      this.noteActions = [...resp['noteTypes']];
    });
    this.candidateOptionsService.getActivityFilter().subscribe((resp: any) => {
      this.showCsvExport = resp.csv;
    });
  }

  ngOnInit() {
    this.calculateTotal();
    setTimeout(() => { // TODO: Putting this on a timer is jank. If it's waiting for something, we should be waiting on a subscription
      this.loadCurrentUserEmail();
    }, 3000);
  }

  // Makes sure that the focus stays in the input field when the calendar pops up.
  refocusInput(input: HTMLInputElement): void {
    setTimeout(() => input.focus(), 100);
  }

  clearDateAddedBefore(): void {
    this.cleanOptionsForm.get('dateAddedBefore')?.setValue(null);
  }

  clearDateLastComment(): void {
    this.cleanOptionsForm.get('dateLastComment')?.setValue(null);
  }

  private createForm(): void {
    const searchPropertiesControls = this.createControlsFromObject(this.cleanOptions);
    this.cleanOptionsForm = this.formBuilder.group({
      ...searchPropertiesControls,
    });
  }

  private createControlsFromObject(obj: SearchProperties): any {
    const controls = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (typeof value === 'boolean') {
        controls[key] = [false];
      } else if (Array.isArray(value)) {
        controls[key] = [[]];
      } else if (value === null || value === undefined) {
        controls[key] = [null];
      } else {
        controls[key] = [value || ''];
      }
    });
    return controls;
  }

  onHasFileAttachmentChange(event: boolean): void {
    if (event) {
      this.cleanOptionsForm.get('noFileAttachment')?.setValue(false);
    }
    this.calculateTotal(event, 'hasFileAttachment');
  }

  onNoFileAttachmentChange(event: boolean): void {
    if (event) {
      this.cleanOptionsForm.get('hasFileAttachment')?.setValue(false);
    }
    this.calculateTotal(event, 'noFileAttachment');
  }

  numRecordsFoundTooMany(): boolean {
    return this.dataCleanerService.numRecordsFoundTooMany();
  }

  isNumRecordsFoundZero(): boolean {
    return this.dataCleanerService.numRecordsFound === 0;
  }

  // TODO: The parent should not be calling methods on the child component and passing the search down this way.
  //  It should be passing the data down to the child via @Input. I want to fix this but I don't have time.
  populateSearch(search: DataCleanerSearch) {
    this.search = search;
    this.currentSearchId = search.id;
    const me = this;
    this.cleanOptions = {...search.searchProperties};
    if (typeof search.searchProperties?.dateAddedWithin === 'string') {
      this.cleanOptions.dateAddedWithin = parseInt(search.searchProperties.dateAddedWithin, 10);
    }
    // This is required because we're storing it as an epoch number but the datepicker requires a Date
    const formData: any = {...this.cleanOptions};
    if (formData.dateLastComment) {
      formData.dateLastComment = new Date(formData.dateLastComment);
    }
    if (formData.dateAddedBefore) {
      formData.dateAddedBefore = new Date(formData.dateAddedBefore);
    }
    this.cleanOptions.dateLastComment = new Date(this.cleanOptions.dateLastComment);
    this.cleanOptionsForm.patchValue(formData);
    this.isScheduled = (search.searchStatus === 'SCHEDULED');
    if (this.isScheduled) {
      this.scheduledSearch = 'later';
      const date = new Date(search.scheduledTime);
      this.scheduledDate = date;
      this.scheduledTime = date.toLocaleTimeString();
    }
    search?.postSearchActions?.forEach(function (psa) {
      if (psa.type === 'EMAIL_NOTIFICATION') {
        me.postSearchActions.EMAIL_NOTIFICATION = true;
        me.emailNotification = psa.details['emailTo'];
      } else if (psa.type === 'RUN_AGAIN') {
        me.postSearchActions.RUN_AGAIN = true;
        me.runAgainOptions.repeatDays = psa.details['repeatDays'];
      } else if (psa.type === 'CREATE_TEARSHEET') {
        me.postSearchActions.CREATE_TEARSHEET = true;
        me.tearsheetGroups = psa.details['groups'] ?? me.tearsheetGroups;
        me.tearsheetOwner = me.users.find(u => u.id === psa.details['owner']?.id);
        me.tearsheetOwnerDisplay = me.tearsheetOwner?.name;
      }
    });
  }

  loadCurrentUserEmail() {
    const me = this;
    this.users.forEach(function (u) {
        if (u.id === me.currentUser.id) {
          me.emailNotification = u.email;
        }
      }
    );
  }

  scheduleLater() {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DATACLEANER_SCHEDULED_RUN);
      return;
    }
    // This is required because we're storing it as an epoch number but the datepicker requires a Date
    this.search.scheduledTime = new Date(this.scheduledDateTime).getTime()
    const postObject = this.getPostObject();
    let searchId = '';
    if (this.currentSearchId) {
      searchId = this.currentSearchId;
    }
    // TODO: this call should be made by the DataCleanerService and initiated by the DataCleanerComponent.
    //  - Emit this data to the parent
    this.http.post(`${ environment.dataCleanerUrl }/dataClean/scheduleSearch?searchId=${ searchId }`, postObject).subscribe((data: any) => {
      this.messageNotificationService.success(data.name + ' was successfully ' + data.action);
      if (this.currentSearchId) {
        this.currentSearchId = '';
        this.resetSearch();
      } else {
        this.currentSearchId = data.searchId;
      }
      this.scheduledSearchEvent.emit();
    }, (err: any) => {
      this.messageNotificationService.error('An internal error occurred', err);
      console.log(err);
    });
  }

  scheduledTimeIsAfterNow() {
    return this.scheduledDateTime > new Date() && this.scheduledDateTime !== null;
  }

  getPostObject() {
    // TODO: This is awful. We should not have to do this. We have an entire form, why are we stitching together multiple sources of truth
    //  at the end? The form should be the only source of truth and we should never have to construct a new source of truth.
    //  This and the entire HTML desperately needs restructured to actually use forms to match what we send to the back-end.
    //  Even better would be to restructure the object we send to the back-end but that's a whole other can of worms.
    const formValues = this.cleanOptionsForm.value;
    // This is required because we're storing it as an epoch number but the datepicker requires a Date
    const updatedCleanOptions = {...formValues};
    if (updatedCleanOptions.dateLastComment instanceof Date) {
      updatedCleanOptions.dateLastComment = updatedCleanOptions.dateLastComment.getTime();
    }
    if (updatedCleanOptions.dateAddedBefore instanceof Date) {
      updatedCleanOptions.dateAddedBefore = updatedCleanOptions.dateAddedBefore.getTime();
    }
    Object.assign(this.cleanOptions, formValues);
    const postObject = {
      searchProperties: this.cleanOptions,
      activityFilters: this.search.activityFilters,
      searchName: formValues.searchName || this.search.searchName,
      entity: this.isCandidate ? 'Candidate' : 'ClientContact',
      scheduledTime: this.search.scheduledTime,
      postSearchActions: []
    };
    if (this.postSearchActions.EMAIL_NOTIFICATION) {
      postObject.postSearchActions.push({type: 'EMAIL_NOTIFICATION', details: {emailTo: this.emailNotification}});
    }
    if (this.postSearchActions.CREATE_TEARSHEET) {
      postObject.postSearchActions.push({
        type: 'CREATE_TEARSHEET',
        details: {groups: this.tearsheetGroups, owner: this.tearsheetOwner}
      });
    }
    if (this.postSearchActions.RUN_AGAIN) {
      postObject.postSearchActions.push({type: 'RUN_AGAIN', details: this.runAgainOptions});
    }
    return postObject;
  }

  runSearch() {
    if (this.adminService.isInLiteMode()) {
      this.datatoolsLiteService.triggerMessage$.next(DataToolsLiteMessage.DATACLEANER_INSTANT_RUN);
      return;
    }
    const postObject = this.getPostObject();
    this.searchIdChange.emit(this.currentSearchId);
    this.runSearchRequested.emit(postObject);
  }

  // TODO: This is unnecessary. The column and row headings should not be part of the data.
  setPeopleGroups(unfilteredPeopleGroups: IColumns[]) {
    this.peopleGroups.options = unfilteredPeopleGroups.filter(column => column.name !== 'group');
  }

  calculateTotal(event?, field?) {
    this.calculateTotalSubscription?.unsubscribe();
    this.isLoading = true;
    // this is seemingly required here in order to change the 'model' as *i think* this is called on the
    // onChange of the state of the element, but before the change has actually been bound to the source variable.
    if (field) {
      this.cleanOptions[field] = event;
      this.cleanOptionsForm.markAsDirty();
    }
    const postObject = this.getPostObject();
    // TODO: this call should be made by the DataCleanerService and initiated by the DataCleanerComponent.
    //  - Emit this data to the parent
    this.calculateTotalSubscription = this.http.post(`${environment.dataCleanerUrl}/dataClean/calculateTotal`, postObject)
      .subscribe(
        (data: any) => {
          this.dataCleanerService.numRecordsFound = data.total;
          this.numRecordsFound = data.total;
        },
        err => {
        },
        () => {
          this.cleanOptions[field] = event;     // update the model
          this.isLoading = false;
        }
      );
  }

  selectSection(event, fields) {
    const siblings = fields.split(',');
    let someAreChecked = false;
    let allAreChecked = false;
    let selectedCount = 0;
    const noOfSiblings = siblings.length;
    // Cycle through, see if any are checked already
    const me = this;
    siblings.forEach(function (s) {
      if (me.cleanOptions[s] === true) {
        someAreChecked = true;
        selectedCount++;
      }
    });
    // If number already selected = number of siblings {
    if (selectedCount === noOfSiblings) {
      allAreChecked = true;
    } else {
      allAreChecked = false;
    }
    // If SOME or NOT ALL are checked, we want them all CHECKED
    if (someAreChecked && !allAreChecked || !allAreChecked) {
      // Start at 1 so we ignore the actual select all button/icon
      siblings.forEach(function (s) {
        me.cleanOptionsForm.get(s)?.setValue(true);
        me.cleanOptions[s] = true;
        event = true;
        allAreChecked = true;
      });
    } else if (allAreChecked === true) { // If ALL are checked, we want them all *UN*checked
      siblings.forEach(function (s) {
        me.cleanOptionsForm.get(s)?.setValue(false);
        me.cleanOptions[s] = false;
        event = false;
        allAreChecked = false;
      });

    }
    this.calculateTotal(event);
  }

  checkRunAgain() {
    const ra = this.postSearchActions.RUN_AGAIN;
    if (ra) {
      if (this.runAgainOptions.repeatDays < 1) {
        return false;
      }
    }
    return true;
  }

  getCreateTearsheetSelected() {
    const tearSheetSelected = this.postSearchActions.CREATE_TEARSHEET;
    const dateAdded = (this.tearsheetGroups?.dateAdded?.length ?? 0) > 0;
    const dateLastComment = (this.tearsheetGroups?.dateLastComment?.length ?? 0) > 0;
    const dateLastActivity = (this.tearsheetGroups?.dateLastActivity?.length ?? 0) > 0;
    return !tearSheetSelected || (
        tearSheetSelected && this.tearsheetOwner &&
        (dateAdded || dateLastComment || dateLastActivity)
    );
  }

  getEmailSelected() {
    const emailNotificationSelected = this.postSearchActions.EMAIL_NOTIFICATION;
    return !emailNotificationSelected || (emailNotificationSelected && this.emailNotification);
  }

  changeScheduledSearch(val: string) {
    this.scheduledSearch = val;
    this.updateChange();
  }

  resetSearch() {
    this.search = {...this.blankSearch};
    this.changeScheduledSearch('now');
    this.cleanOptions = new SearchProperties();
    this.isScheduled = false;
    const defaultValues = this.createControlsFromObject(new SearchProperties());
    const resetValues = {};
    Object.keys(defaultValues).forEach(key => {
      resetValues[key] = defaultValues[key][0];
    });
    this.cleanOptionsForm.reset(resetValues);
    this.tearsheetOwner = null;
    this.tearsheetOwnerDisplay = '';
    this.postSearchActions = {
      EMAIL_NOTIFICATION: false,
      CREATE_TEARSHEET: false,
      RUN_AGAIN: false
    };
    this.tearsheetGroups = {
      dateAdded: [],
      dateLastComment: [],
      dateLastActivity: []
    };
    this.scheduledDate = null;
    this.scheduledTime = null;
    this.runAgainOptions.repeatDays = null;
    this.calculateTotal();
    this.cleanOptionsForm.markAsPristine();
  }

  filterUsers(searchTerm: string): void {
    this.filteredUsers = searchTerm ?
        this.users.filter(user => user?.name?.toLowerCase().includes(searchTerm.toLowerCase())) :
        this.users;
  }

  onUserSelected(user: any): void {
    this.tearsheetOwner = user;
    this.tearsheetOwnerDisplay = user.name;
    this.updateChange();
  }

  updateChange() {
    this.cleanOptionsForm.markAsDirty();
  }
}
